import { Model, type ModelProps } from '@/infra/model'
import { Id } from '@/infra/id'
import { computed, observable } from 'mobx'
import { unixEpoch } from '@/utils/date-util'
import { links } from '@/utils/link'
import type { User } from '@/features/iam/users/User'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import { stripMinidown } from '@/minidown/strip'

export type NotificationProps = ModelProps<Notification>

/**
 * Notification
 */
export class Notification extends Model<Notification> {
  id = Id.none

  @observable accessor message = ''
  @observable accessor dispatchedAt = unixEpoch()
  @observable accessor image: NotificationImage = { type: 'None' }
  @observable accessor action: NotificationAction = { type: 'None' }
  @observable accessor workspace = Workspace.none
  @observable accessor status: NotificationStatus = 'Sent'

  /**
   * Image URL to show for the notification, if any.
   */
  @computed
  get imageUrl() {
    const defaultImage = this.workspace.branding.logos.icon
    const image = this.image
    if (!image) {
      return defaultImage
    }

    switch (image.type) {
      case 'Static':
        return image.url
      case 'UserAvatar':
        return image.user.avatarUrl
      default:
        return defaultImage
    }
  }

  /**
   * URL to navigate to when the notification is interacted with.
   */
  @computed
  get actionUrl() {
    const action = this.action
    if (!action) {
      return null
    }

    switch (action.type) {
      case 'ViewJob':
        return links.jobDetails(action.jobId)
      default:
        return null
    }
  }

  /**
   * The notification text without any formatting.
   */
  @computed
  get plainText() {
    return stripMinidown(this.message)
  }
}

export interface NoImage {
  type: 'None'
}

export interface StaticImage {
  type: 'Static'
  url: string
}

export interface UserAvatarImage {
  type: 'UserAvatar'
  user: User
}

export declare type NotificationImage = NoImage | StaticImage | UserAvatarImage

export interface DoNothingAction {
  type: 'None'
}

export interface ViewJobAction {
  type: 'ViewJob'
  jobId: string
}

export type NotificationAction = DoNothingAction | ViewJobAction

export type NotificationStatus = 'Queued' | 'Sent' | 'Read'
